import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const tjenester = [
  { name: 'Ny nettside', href: '/tjenester/nettside' },
  { name: 'Annonsering', href: '/tjenester/annonsering' },
  { name: 'SEO', href: '/tjenester/seo' },
  { name: 'Nettbutikk', href: '/tjenester/nettbutikk' },
  { name: 'Logodesign', href: '/tjenester/logodesign' },
  { name: 'Google Bedriftsprofil', href: '/tjenester/google-bedriftsprofil' },
];

export function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tjenesterOpen, setTjenesterOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const isActive = (path) => location.pathname === path;
  const isTjenesterActive = location.pathname.startsWith('/tjenester');
  const isBloggActive = location.pathname.startsWith('/blogg');

  // Lukk desktop-dropdown når man klikker utenfor – IKKE når mobilmeny er åpen
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen) return; // Mobilmeny åpen: ikke lukk – ellers lukkes tjenester før tap fullføres
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setTjenesterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setTjenesterOpen(false);
  }, [location.pathname]);

  return (
    <nav 
      className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b border-border/50"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center hover:opacity-80 transition-opacity duration-300"
            data-testid="navbar-logo"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_portfolio-site-106/artifacts/kfpt7w0i_logo%20heter.png"
              alt="Side Kraft – Digitalt markedsføringsbyrå"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-10">
            <Link 
              to="/" 
              className={`font-manrope text-sm font-medium tracking-wide uppercase transition-colors duration-300 ${isActive('/') ? 'text-accent' : 'text-primary hover:text-accent'}`}
              data-testid="nav-home"
            >
              Hjem
            </Link>
            
            {/* Tjenester Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button 
                className={`font-manrope text-sm font-medium tracking-wide uppercase flex items-center gap-1 transition-colors duration-300 ${isTjenesterActive ? 'text-accent' : 'text-primary hover:text-accent'}`}
                onClick={() => setTjenesterOpen(!tjenesterOpen)}
                data-testid="nav-tjenester"
              >
                Tjenester
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${tjenesterOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {tjenesterOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-card border border-border shadow-lg">
                  <div className="py-2">
                    {tjenester.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={`block px-6 py-3 font-manrope text-sm transition-colors duration-200 ${isActive(item.href) ? 'text-accent bg-muted' : 'text-primary hover:text-accent hover:bg-muted'}`}
                        data-testid={`nav-${item.href.split('/').pop()}`}
                        onClick={() => setTjenesterOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link 
              to="/om-oss" 
              className={`font-manrope text-sm font-medium tracking-wide uppercase transition-colors duration-300 ${isActive('/om-oss') ? 'text-accent' : 'text-primary hover:text-accent'}`}
              data-testid="nav-om-oss"
            >
              Om oss
            </Link>

<Link 
              to="/prosjekter" 
              className={`font-manrope text-sm font-medium tracking-wide uppercase transition-colors duration-300 ${isActive('/prosjekter') ? 'text-accent' : 'text-primary hover:text-accent'}`}
              data-testid="nav-prosjekter"
            >
              Prosjekter
            </Link>

            <Link 
              to="/blogg" 
              className={`font-manrope text-sm font-medium tracking-wide uppercase transition-colors duration-300 ${isBloggActive ? 'text-accent' : 'text-primary hover:text-accent'}`}
              data-testid="nav-blogg"
            >
              Blogg
            </Link>

            <Link 
              to="/kontakt"
              className="font-syne font-bold text-sm uppercase tracking-wide px-6 py-3 bg-accent text-accent-foreground hover:bg-primary transition-colors duration-300"
              data-testid="nav-kontakt"
            >
              Ta kontakt
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - originalt design */}
      <div 
        className={`lg:hidden absolute top-full left-0 w-full bg-background border-b border-border transition-all duration-300 ${mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}`}
        data-testid="mobile-menu"
      >
        <div className="px-6 py-6 space-y-4">
          <Link 
            to="/" 
            className="block font-manrope text-sm font-medium uppercase py-2"
            onClick={() => setMobileMenuOpen(false)}
          >
            Hjem
          </Link>
          
          <div>
            <button 
              className="flex items-center justify-between w-full font-manrope text-sm font-medium uppercase py-2"
              onClick={() => setTjenesterOpen(!tjenesterOpen)}
            >
              Tjenester
              <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${tjenesterOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {tjenesterOpen && (
              <div className="pl-4 mt-2 space-y-2 border-l-2 border-accent">
                {tjenester.map((item) => (
                  <button
                    key={item.href}
                    type="button"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate(item.href);
                    }}
                    className="block w-full text-left font-manrope text-sm py-2 text-muted-foreground hover:text-primary"
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Link 
            to="/om-oss" 
            className="block font-manrope text-sm font-medium uppercase py-2"
            onClick={() => setMobileMenuOpen(false)}
          >
            Om oss
          </Link>

<Link 
            to="/prosjekter" 
            className="block font-manrope text-sm font-medium uppercase py-2"
            onClick={() => setMobileMenuOpen(false)}
          >
            Prosjekter
          </Link>

          <Link 
            to="/blogg" 
            className="block font-manrope text-sm font-medium uppercase py-2"
            onClick={() => setMobileMenuOpen(false)}
          >
            Blogg
          </Link>

          <Link 
            to="/kontakt"
            className="block font-syne font-bold text-sm uppercase tracking-wide px-6 py-3 bg-accent text-accent-foreground text-center mt-4"
            onClick={() => setMobileMenuOpen(false)}
          >
            Ta kontakt
          </Link>
        </div>
      </div>
    </nav>
  );
}
